import { ChatMessage } from '@chewy/chat-library-common';
import { type DateStatus, type TagDef, itemTagMap } from '../types/types';

/** Maps API tag strings to Kib badge types; omit `label` so `Tags` passes `true` and Kib uses its default copy per type. */
export const getFilteredItemTags = (tags: string[]): TagDef[] =>
  tags?.map(tag => ({ Icon: itemTagMap[tag.toUpperCase()] || '' })).filter(tag => tag.Icon !== '');

export const messageHasWidgetType = (message: ChatMessage, widgetType: string | string[]) => {
  const widgetTypes = Array.isArray(widgetType) ? widgetType : [widgetType];

  return widgetTypes.some(
    widgetTypeItem =>
      Array.isArray(message.content) &&
      message.content.some(
        ({ widgetType }) => widgetType && widgetType.toLowerCase() === widgetTypeItem.toLowerCase()
      )
  );
};

export const hasDateStatus = (dateStatus?: DateStatus) =>
  Boolean(dateStatus?.status || dateStatus?.label);

/** Whether this message is the beginning of a new exchange */
export const isExchangeMessage = (message: ChatMessage): boolean => {
  return message?.responseType === 'user';
};

/**
 * Whether this message delineates a transition to a human agent.
 */
export const isAgentTransferMessage = (message: ChatMessage): boolean => {
  const newStageTypes: ChatMessage['type'][] = ['transferring', 'transferred', 'agentjoined'];
  return messageHasWidgetType(message, newStageTypes);
};

/** RFC 5321-ish practical limits; keeps work bounded on untrusted input. */
export const MAX_EMAIL_LENGTH = 254;

/**
 * Linear-time email shape check (no backtracking-heavy regex on user input).
 * Matches the intent of the previous pattern: one @, no whitespace, domain with dot-separated non-empty labels.
 */
export const isValidEmailFormat = (value: string): boolean => {
  if (value.length > MAX_EMAIL_LENGTH) {
    return false;
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return false;
  }
  for (let i = 0; i < trimmed.length; i += 1) {
    const code = trimmed.charCodeAt(i);
    if (
      code <= 0x20 ||
      code === 0x7f ||
      code === 0xa0 ||
      code === 0x1680 ||
      (code >= 0x2000 && code <= 0x200a) ||
      code === 0x2028 ||
      code === 0x2029 ||
      code === 0x202f ||
      code === 0x205f ||
      code === 0x3000 ||
      code === 0xfeff
    ) {
      return false;
    }
  }
  const at = trimmed.indexOf('@');
  if (at <= 0 || at !== trimmed.lastIndexOf('@')) {
    return false;
  }
  const local = trimmed.slice(0, at);
  const domain = trimmed.slice(at + 1);
  if (local.length === 0 || local.length > 64 || domain.length === 0 || domain.length > 253) {
    return false;
  }
  if (!domain.includes('.')) {
    return false;
  }
  for (const label of domain.split('.')) {
    if (label.length === 0) {
      return false;
    }
  }
  return true;
};
